import React, { useContext } from 'react';
import { SubscriptionPlan } from '@dayetopia/types';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import { ONE_MONTH_INTERVAL_LENGTH, ProductCode } from '@contracts';
import TickIcon from '@images/multi-month/tick-icon-white.svg';
import { getPriceText } from '@utils/price';
import { getDiscountPercent } from '@utils/subscriptions';
import PriceDisplay from '../PriceDisplay';

interface Props {
  subscriptions: SubscriptionPlan[];
  productCode: ProductCode;
  selectedSubscription: SubscriptionPlan;
  onOptionSelect: (plan: SubscriptionPlan) => void;
}

const OptionBoxes: React.FC<Props> = ({ subscriptions, productCode, selectedSubscription, onOptionSelect }) => {
  const { t } = useTranslation();
  const { state } = useContext(Context);
  const { currency, country } = state;

  const getPaymentKey = (subscription: SubscriptionPlan) => {
    if (subscription.billing.installments > 1) {
      return 'components.subscription-options.pay-multiple-installments';
    }
    if (subscription.subscriptionInterval.length === ONE_MONTH_INTERVAL_LENGTH) {
      return 'components.subscription-options.pay-single-installment-monthly';
    }
    return 'components.subscription-options.pay-single-installment';
  };

  const renderOptionText = (subscription: SubscriptionPlan) => {
    if (productCode === ProductCode.ScreeningKit) {
      return t('components.subscription-options.tests-per-year-2', { count: subscription.delivery.installments });
    }
    return t(
      subscription.subscriptionInterval.length > ONE_MONTH_INTERVAL_LENGTH
        ? 'components.subscription-options.boxes.delivery-multi-month'
        : 'components.subscription-options.boxes.delivery-single-month',
      { length: subscription.subscriptionInterval.length }
    );
  };

  return (
    <OptionBoxesWrap id="multi-month-option-boxes" className={`product-${productCode} count-${subscriptions.length}`}>
      {subscriptions.map((subscription, index: number) => {
        const isSelected =
          selectedSubscription.planCode === subscription.planCode &&
          selectedSubscription.delivery.installments === subscription.delivery.installments;
        return (
          <OptionBox key={`${subscription.planCode}-${index}`} onClick={() => onOptionSelect(subscription)} $selectedOption={!!isSelected}>
            <OptionText $selectedOption={!!isSelected}>
              {isSelected && <TickIcon />}
              {renderOptionText(subscription)}
            </OptionText>
            <PriceWrap>
              <PriceInner color="transparent">
                <PriceDisplay price={subscription.discountedPrice ?? 0} currency={currency} />
              </PriceInner>
              <Span>{t('components.subscription-options.per-month')} </Span>
              {subscription.subscriptionInterval.length > ONE_MONTH_INTERVAL_LENGTH && (
                <Pay>
                  <Trans
                    i18nKey={getPaymentKey(subscription)}
                    values={{
                      installments: subscription.billing.installments,
                      currency: '',
                      installmentPrice: getPriceText(subscription.billing.installmentPrice ?? 0, country, currency, t, false, false).fullString
                    }}
                  />
                </Pay>
              )}
            </PriceWrap>
            <Divider />
            <BenefitsWrap className="benefits-wrap">
              <Save>
                {t('components.subscription-options.save', {
                  percent: Math.round(getDiscountPercent(subscription, country))
                })}
              </Save>
              {subscription.freeItem && (
                <FreeItemBox className={`key-${subscription.freeItem.key}`}>
                  {t(`components.subscription-options.${subscription.freeItem.key}`)}
                </FreeItemBox>
              )}
            </BenefitsWrap>
          </OptionBox>
        );
      })}
    </OptionBoxesWrap>
  );
};

export default OptionBoxes;

const OptionBoxesWrap = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  padding-bottom: 15px;
  justify-content: flex-start;
  justify-content: space-around;

  @media (max-width: 1024px) {
    gap: 12px;
  }

  &.product-tampon_box .benefits-wrap {
    height: 38px;
  }
`;

const OptionBox = styled.div<{ $selectedOption?: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 100px;
  flex-grow: 1;
  gap: 5px;
  padding-bottom: 5px;
  border: ${({ $selectedOption }) => ($selectedOption ? `2px solid ${Colors.gold}` : `1px solid ${Colors.gold}`)};
  border-radius: 2px;

  &.count-3 {
    @media (max-width: 1024px) {
      max-width: calc(33% - 17px);
      min-width: calc(33% - 17px);
    }
  }

  &:first-child {
    margin-left: 15px;
  }

  &:last-child {
    margin-right: 15px;
  }
`;

const OptionText = styled.div<{ $selectedOption?: boolean; $textColor?: string }>`
  display: flex;
  justify-content: center;
  width: 100%;
  align-items: center;
  border-bottom: 1px solid ${Colors.gold};
  height: 37px;
  background-color: ${({ $selectedOption }) => ($selectedOption ? Colors.gold : '')};
  color: ${({ $selectedOption, $textColor }) => ($selectedOption ? Colors.cream : $textColor)};

  font-family: BrownStd;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 17px;
  letter-spacing: 0;
  margin-bottom: 2px;

  svg {
    margin-right: 4px;
  }
`;

const Divider = styled.div`
  background: ${Colors.gold};
  height: 1px;
  width: 100%;
`;

const BenefitsWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 24px;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const Save = styled.p`
  background: ${Colors.lilac};
  color: ${Colors.cream};
  font-family: BrownStd;
  font-weight: 700;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 3px 5px;
  border-radius: 2px;
`;

const PriceWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  justify-content: center;
  padding: 4px 0;
`;

const PriceInner = styled.p`
  font-family: Domaine;
  font-size: 18px;
  line-height: 110%;
  font-style: normal;
  font-weight: 500;
  text-align: center;
  color: inherit;

  @media (max-width: 1024px) {
    font-size: 18px;
  }
`;

const Pay = styled.p`
  padding-inline: 8px;
  font-family: BrownStd;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: 0;
  text-align: center;
  color: inherit;
  margin-top: 5px;
`;

const Span = styled.span`
  opacity: 0.5;
  color: inherit;
  font-family: BrownStd;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0;
  text-align: right;
  white-space: nowrap;
`;

const FreeItemBox = styled(P)`
  font-weight: 700;
  font-size: 10px;
  line-height: 100%;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  &.key-flexible {
    display: none;
  }
`;
